import React, { useState, useEffect } from 'react';
import { getSecureFileUrl } from '../utils/fileUtils';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

// On-demand audio player
export const SecureAudioPlayer = ({ filePath, isPlaying }) => {
    const [audioSrc, setAudioSrc] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const audioRef = React.useRef(null);

    // Effect to handle global play events
    useEffect(() => {
        const handleGlobalPlay = (event) => {
            if (audioRef.current && event.detail.target !== audioRef.current) {
                audioRef.current.pause();
            }
        };

        document.addEventListener('audioplay', handleGlobalPlay);

        return () => {
            document.removeEventListener('audioplay', handleGlobalPlay);
        };
    }, []);

    // Effect to pause audio when isPlaying becomes false
    useEffect(() => {
        if (!isPlaying && audioRef.current) {
            audioRef.current.pause();
        }
    }, [isPlaying]);

    const loadAudio = async () => {
        setIsLoading(true);
        setError('');
        try {
            const url = await getSecureFileUrl(filePath);
            if (url) {
                setAudioSrc(url);
            } else {
                setError('Не удалось загрузить аудиофайл.');
            }
        } catch (e) {
            console.error("Failed to load audio", e);
            setError('Ошибка при загрузке аудио.');
        } finally {
            setIsLoading(false);
        }
    };

    // Cleanup blob URL when component unmounts
    useEffect(() => {
        return () => {
            if (audioSrc) {
                URL.revokeObjectURL(audioSrc);
            }
        };
    }, [audioSrc]);

    // onPlay handler for the audio element
    const handlePlay = () => {
        const event = new CustomEvent('audioplay', { detail: { target: audioRef.current } });
        document.dispatchEvent(event);
    };

    if (error) {
        return <p className="text-danger small">{error}</p>;
    }

    if (audioSrc) {
        return <audio ref={audioRef} controls autoPlay src={audioSrc} className="w-100" style={{ maxWidth: '500px' }} onPlay={handlePlay}></audio>;
    }

    return (
        <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={loadAudio} 
            disabled={isLoading}
            className="w-100"
            style={{ maxWidth: '500px' }}
        >
            {isLoading ? 'Загрузка...' : <><i className="fas fa-play me-2"></i>Включить</>}
        </Button>
    );
};

// On-demand file download link
export const SecureFileLink = ({ filePath, filename, children }) => {
    const [isDownloading, setIsDownloading] = useState(false);

    const downloadFile = async (e) => {
        e.preventDefault();
        setIsDownloading(true);
        try {
            const url = await getSecureFileUrl(filePath);
            if (url) {
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename || 'download');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url); // Clean up immediately after download starts
            } else {
                toast.error('Не удалось загрузить файл для скачивания.');
            }
        } catch (error) {
            console.error("Failed to download file", error);
            toast.error('Ошибка при скачивании файла.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <a href="#" onClick={downloadFile} className={isDownloading ? 'text-muted pe-none' : ''}>
            {isDownloading ? 'Загрузка...' : children}
        </a>
    );
};